"use client";

/**
 * Shared hotel-booking modal wiring for SmartButton/SmartLink — both previously duplicated the
 * dynamic import, open state, and modal-render JSX independently. Each component's trigger markup
 * (button vs. wrapped children, styling, touch handling) still lives in its own file since that
 * genuinely differs; this hook owns only the part that didn't need to.
 */

import { useCallback, useState } from "react";
import dynamic from "next/dynamic";

const HotelBookingModal = dynamic(
  () => import("../_components/public/HotelBookingModal").then((mod) => mod.HotelBookingModal),
  { ssr: false }
);

/**
 * @param eventId - Event the booking link belongs to
 * @param bookingLinkId - Booking link to open, or `null` if the current href isn't a hotel-booking link
 * @returns `open` to call on activation, and `modal` — the modal element (or `null` when closed/no link) to render alongside the trigger
 */
export function useHotelBookingCta(eventId: number, bookingLinkId: number | null) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const modal =
    isOpen && bookingLinkId ? (
      <HotelBookingModal isOpen={isOpen} onClose={close} eventId={eventId} bookingLinkId={bookingLinkId} />
    ) : null;

  return { open, modal };
}
